import {useEffect, useMemo, useRef, useState} from "react"
import {Button, Checkbox, Divider, Dropdown, Menu, Modal, Spin} from "antd"
import {useTranslation} from "react-i18next"
import {useHistory} from "react-router-dom"
import {useToggle} from "react-use"
import cx from "classnames"
import {map} from "lodash"
import {useEdularModulesContext, Modules} from "@edular/modules"
import {calendarIntegrationService} from "services"
import {useQueryClient} from "@tanstack/react-query"
import {useModel, useMutation, useMyIntegrationAccounts, MY_INTEGRATION_ACCOUNTS_QUERY_KEY} from "hooks"
import {handleError, toastError, toastInfo, toastSuccess} from "helpers"
import {
    ExternalCalendarProvider,
    GenerateExternalCalendarOAuthLinkParams,
    IntegrationAccount,
    LinkedCalendarInfo,
    StaticOption
} from "types/calendar"
import {
    CALENDAR_PROVIDER_LABELS,
    ConnectedAccountGroup,
    getCalendarConnectNotice,
    groupConnectedCalendars,
    parseCalendarConnectResult,
    stripCalendarConnectParams
} from "utils/connectedCalendars"
import {Icon} from "components/Icon"
import googleLogoImg from "assets/images/google-logo.svg"
import microsoftLogoImg from "assets/images/microsoft-logo.svg"
import {ManageCalendarsModal} from "./ManageCalendarsModal"
import styles from "./ConnectedCalendars.module.css"

type Props = {
    linkedCalendars: LinkedCalendarInfo[]
    staticOptions: StaticOption[]
    updateLinkedCalendars: (data: LinkedCalendarInfo[]) => void
    updateStaticOptions: (data: StaticOption[]) => void
}

export const ConnectedCalendars = ({
    linkedCalendars,
    staticOptions,
    updateLinkedCalendars,
    updateStaticOptions
}: Props) => {
    const {t} = useTranslation(["calendar"])
    const {isModuleEnable} = useEdularModulesContext()
    const providers: {id: ExternalCalendarProvider; label: string}[] = [
        isModuleEnable(Modules.OutlookCalendar) && {id: ExternalCalendarProvider.Outlook, label: "Outlook"},
        isModuleEnable(Modules.GoogleCalendar) && {id: ExternalCalendarProvider.Google, label: "Google"}
    ].filter(Boolean)
    const [showContent, toggleShowContent] = useToggle(true)
    const model = useModel()
    const queryClient = useQueryClient()
    const isOwnCalendar = !model.viewOtherUserCalendar
    const {accounts} = useMyIntegrationAccounts({enabled: isOwnCalendar && providers.length > 0})
    const [managedAccount, setManagedAccount] = useState<IntegrationAccount | null>(null)
    const delayedReloadRef = useRef<ReturnType<typeof setTimeout>>()
    const history = useHistory()
    const connectResultHandledRef = useRef(false)

    const edularCalendars = linkedCalendars.filter((item) => item.isPrimary || !item.provider)
    const accountGroups = useMemo(
        () => groupConnectedCalendars(linkedCalendars, isOwnCalendar ? accounts : null),
        [linkedCalendars, accounts, isOwnCalendar]
    )
    const hasReconnectAccounts = accountGroups.some((group) => group.requiresReconnect)

    useEffect(() => () => clearTimeout(delayedReloadRef.current), [])

    const hasPendingCalendars = accountGroups.some((group) => group.pendingCalendars.length > 0)

    // Keep checking until Edular has created every newly synced calendar
    useEffect(() => {
        if (!hasPendingCalendars) {
            return
        }
        const interval = setInterval(() => queryClient.invalidateQueries(["my-linked-calendars"]), 5000)
        return () => clearInterval(interval)
    }, [hasPendingCalendars, queryClient])

    // Edular creates a calendar for a newly synced provider calendar asynchronously,
    // so the calendar list is reloaded once more shortly after a change
    const reloadCalendars = () => {
        queryClient.invalidateQueries(MY_INTEGRATION_ACCOUNTS_QUERY_KEY)
        queryClient.invalidateQueries(["my-linked-calendars"])
        clearTimeout(delayedReloadRef.current)
        delayedReloadRef.current = setTimeout(() => {
            queryClient.invalidateQueries(MY_INTEGRATION_ACCOUNTS_QUERY_KEY)
            queryClient.invalidateQueries(["my-linked-calendars"])
        }, 3000)
    }

    const generateOAuthMutation = useMutation(
        (data: GenerateExternalCalendarOAuthLinkParams) => calendarIntegrationService.generateOAuth(data),
        {
            onSuccess: ({authUrl}) => window.location.assign(authUrl),
            onError: (error) => handleError(error)
        }
    )

    // The provider sign-in redirects back here with its result in the URL
    useEffect(() => {
        if (connectResultHandledRef.current) {
            return
        }
        connectResultHandledRef.current = true

        const connectResult = parseCalendarConnectResult(window.location.search)
        if (!connectResult) {
            return
        }

        // Remove the result from the URL so a reload doesn't show the message again
        const cleanUrl = new URL(stripCalendarConnectParams(window.location.href))
        history.replace({
            pathname: cleanUrl.pathname,
            search: cleanUrl.search,
            hash: cleanUrl.hash,
            state: history.location.state
        })

        const {type, messageKey} = getCalendarConnectNotice(connectResult)
        const message = t(messageKey, {provider: CALENDAR_PROVIDER_LABELS[connectResult.provider]})
        if (type === "success") {
            toastSuccess(message)
            reloadCalendars()
        } else if (type === "info") {
            toastInfo(message)
        } else {
            toastError(message)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const connectAccount = (provider: ExternalCalendarProvider) => {
        generateOAuthMutation.mutate({provider, successRedirect: stripCalendarConnectParams(window.location.href)})
    }

    const disconnectMutation = useMutation(
        (data: {calendarId: number}) => calendarIntegrationService.disconnectUserCalendars(data),
        {
            onSuccess: (_, payload) => {
                const newLinkedCalendar = linkedCalendars.filter((item) => item.id !== payload.calendarId)
                updateLinkedCalendars(newLinkedCalendar)
            },
            onError: (error) => handleError(error)
        }
    )
    const disconnectAccountMutation = useMutation(
        (data: {accountId: number}) => calendarIntegrationService.disconnectAccount(data),
        {
            onSuccess: (_, payload) => {
                const accountCalendarIds = new Set(
                    accountGroups
                        .find((group) => group.accountId === payload.accountId)
                        ?.calendars.map((item) => item.calendarId) ?? []
                )
                updateLinkedCalendars(linkedCalendars.filter((item) => !accountCalendarIds.has(item.calendarId)))
                reloadCalendars()
            },
            onError: (error) => handleError(error)
        }
    )

    const confirmDisconnectAccount = (group: ConnectedAccountGroup) => {
        Modal.confirm({
            title: t("calendar.connectedCalendars.disconnectAccountTitle", {account: group.name}),
            content: t("calendar.connectedCalendars.disconnectAccountDescription"),
            okText: t("calendar.connectedCalendars.disconnectAccount"),
            okButtonProps: {danger: true},
            cancelText: t("calendar.connectedCalendars.cancel"),
            onOk: () => disconnectAccountMutation.mutateAsync({accountId: group.accountId})
        })
    }

    const toggleLinkCalendar = (data: LinkedCalendarInfo, checked: boolean) => {
        if (data.requiresReconnect) {
            return
        }
        const newLinkedCalendars = linkedCalendars.map((item) => {
            if (item.calendarId === data.calendarId) {
                return {...item, isShow: checked}
            }
            return item
        })
        updateLinkedCalendars(newLinkedCalendars)
    }

    const toggleStaticOption = (data: StaticOption, checked: boolean) => {
        const newStaticOptions = staticOptions.map((item) => {
            if (item.id === data.id) {
                return {...item, isShow: checked}
            }
            return item
        })
        updateStaticOptions(newStaticOptions)
    }

    const allStaticOptionsChecked = staticOptions.length > 0 && staticOptions.every((item) => item.isShow)
    const someStaticOptionsChecked = staticOptions.some((item) => item.isShow)

    const toggleAllStaticOptions = (checked: boolean) => {
        updateStaticOptions(staticOptions.map((item) => ({...item, isShow: checked})))
    }

    const menu = (
        <Menu className={styles.menuActionWrap}>
            {providers.map((provider, index) => (
                <Menu.Item key={index} className={styles.menuActionItem} onClick={() => connectAccount(provider.id)}>
                    {provider.label}
                </Menu.Item>
            ))}
        </Menu>
    )

    const renderEdularCalendar = (item: LinkedCalendarInfo) => {
        const {id, name, isShow, calendarId} = item

        return (
            <div key={id || calendarId} className={styles.itemInfo}>
                <Checkbox checked={isShow} onChange={(event) => toggleLinkCalendar(item, event.target.checked)} />
                <div className={styles.itemContent}>
                    <span className={styles.itemLabel}>{name}</span>
                </div>
            </div>
        )
    }

    const renderAccountMenu = (group: ConnectedAccountGroup) => (
        <Menu className={styles.menuActionWrap}>
            {group.account?.isProviderListAvailable && !group.requiresReconnect && (
                <Menu.Item
                    key="manage"
                    className={styles.menuActionItem}
                    onClick={() => setManagedAccount(group.account)}>
                    {t("calendar.connectedCalendars.manageCalendars")}
                </Menu.Item>
            )}
            <Menu.Item
                key="disconnect"
                className={cx(styles.menuActionItem, styles.menuActionItemDanger)}
                onClick={() => confirmDisconnectAccount(group)}>
                {t("calendar.connectedCalendars.disconnectAccount")}
            </Menu.Item>
        </Menu>
    )

    const renderAccountGroup = (group: ConnectedAccountGroup) => {
        const canManage = isOwnCalendar && group.accountId != null

        return (
            <div key={group.key} className={styles.accountGroup}>
                <div
                    className={cx(styles.accountHeader, {
                        [styles.itemInfoWarning]: group.requiresReconnect
                    })}>
                    <img
                        src={group.provider === ExternalCalendarProvider.Google ? googleLogoImg : microsoftLogoImg}
                        className={styles.itemLogo}
                        alt="logo"
                    />
                    <div className={styles.itemContent}>
                        <span className={cx(styles.itemLabel, styles.accountName)}>{group.name}</span>
                        {group.requiresReconnect && (
                            <div className={styles.warningInfo}>
                                <div className={styles.warningLabel}>
                                    <Icon icon="WARNING_TRIANGLE_FILL" color="#fa8c16" className={styles.warningIcon} />
                                    <span>{t("calendar.connectedCalendars.reconnectRequired")}</span>
                                </div>
                                <span className={styles.warningDescription}>
                                    {t("calendar.connectedCalendars.reconnectDescription")}
                                </span>
                            </div>
                        )}
                    </div>
                    {group.requiresReconnect && isOwnCalendar && (
                        <Button
                            className={cx(styles.actionBtn, styles.reconnectBtn)}
                            onClick={() => connectAccount(group.provider)}
                            disabled={generateOAuthMutation.isLoading}
                            loading={generateOAuthMutation.isLoading}>
                            {t("calendar.connectedCalendars.reconnect")}
                        </Button>
                    )}
                    {canManage && (
                        <Dropdown overlay={renderAccountMenu(group)} trigger={["click"]}>
                            <Button
                                className={styles.actionBtn}
                                aria-label={t("calendar.connectedCalendars.accountActions")}
                                icon={<Icon icon="MORE_HORZ" color="#000000d9" className={styles.icon} />}
                            />
                        </Dropdown>
                    )}
                </div>
                {group.calendars.map((item) => (
                    <div key={item.calendarId} className={cx(styles.itemInfo, styles.accountCalendar)}>
                        <Checkbox
                            checked={item.isShow}
                            disabled={item.requiresReconnect}
                            onChange={(event) => toggleLinkCalendar(item, event.target.checked)}
                        />
                        <span className={styles.colorDot} style={{background: item.color || undefined}} />
                        <div className={styles.itemContent}>
                            <span className={styles.itemLabel}>{item.name}</span>
                        </div>
                        {isOwnCalendar && group.accountId == null && (
                            <Button
                                className={styles.actionBtn}
                                icon={<Icon icon="CLOSE" color="#000" className={styles.icon} />}
                                onClick={() => disconnectMutation.mutate({calendarId: item.id})}
                                disabled={disconnectMutation.isLoading}
                                loading={disconnectMutation.isLoading}
                            />
                        )}
                    </div>
                ))}
                {group.pendingCalendars.map((item) => (
                    <div key={item.providerCalendarId} className={cx(styles.itemInfo, styles.accountCalendar)}>
                        <Spin size="small" />
                        <span className={styles.colorDot} style={{background: item.color || undefined}} />
                        <div className={styles.itemContent}>
                            <span className={styles.itemLabel}>{item.name}</span>
                            <span className={styles.itemMeta}>{t("calendar.connectedCalendars.syncing")}</span>
                        </div>
                    </div>
                ))}
                {isOwnCalendar && group.availableCount > 0 && !group.requiresReconnect && (
                    <button
                        type="button"
                        className={styles.moreAvailable}
                        onClick={() => setManagedAccount(group.account)}>
                        {t("calendar.connectedCalendars.moreAvailable", {count: group.availableCount})}
                    </button>
                )}
            </div>
        )
    }

    const renderStaticOption = (item: StaticOption) => {
        const {id, name, isShow} = item
        return (
            <div key={id} className={styles.itemInfo}>
                <Checkbox checked={isShow} onChange={(event) => toggleStaticOption(item, event.target.checked)} />
                <span className={styles.itemLabel}>{name}</span>
            </div>
        )
    }

    const renderContent = () => {
        if (!showContent) {
            return null
        }
        return (
            <div className={styles.content}>
                {hasReconnectAccounts && (
                    <div className={styles.warningBanner}>
                        <Icon icon="WARNING_TRIANGLE_FILL" color="#fa8c16" className={styles.warningBannerIcon} />
                        <span>{t("calendar.connectedCalendars.reconnectBanner")}</span>
                    </div>
                )}
                {map(edularCalendars, (item) => renderEdularCalendar(item))}
                {accountGroups.length > 0 && <Divider className={styles.divider} />}
                {map(accountGroups, (group) => renderAccountGroup(group))}
                <Divider />
                <Checkbox
                    checked={allStaticOptionsChecked}
                    indeterminate={someStaticOptionsChecked && !allStaticOptionsChecked}
                    onChange={(event) => toggleAllStaticOptions(event.target.checked)}>
                    {t("calendar.connectedCalendars.selectAll")}
                </Checkbox>
                {map(staticOptions, (item) => renderStaticOption(item))}
            </div>
        )
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.header}>
                <p className={styles.title}>{t("calendar.connectedCalendars.title")}</p>
                <div className={styles.actions}>
                    {!model.viewOtherUserCalendar && (
                        <Dropdown overlay={menu} trigger={["click"]}>
                            <Button
                                className={cx(styles.actionBtn, styles.actionBtn__add)}
                                icon={<Icon icon="PLUS" color="#000000d9" className={styles.icon} />}
                            />
                        </Dropdown>
                    )}
                    <Button
                        className={styles.actionBtn}
                        icon={
                            <Icon
                                icon="ARROW_DOWN"
                                color="#000000d9"
                                className={cx(styles.icon, {[styles.iconRotate]: !showContent})}
                            />
                        }
                        onClick={toggleShowContent}
                    />
                </div>
            </div>
            {renderContent()}
            {managedAccount && (
                <ManageCalendarsModal
                    account={managedAccount}
                    onClose={() => setManagedAccount(null)}
                    onSaved={reloadCalendars}
                />
            )}
        </div>
    )
}
